import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, timeout } from 'rxjs/operators';
import { QuestionsService } from '../../../services/questions.service';
import { Router } from '@angular/router';
import { QuestionnaireInstanceService } from '../../../api/services/questionnaire-instance.service';
import { QuestionComponent } from '../../../components/question/question.component';
import { TranslationEventService } from '../../../services/translation-event.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  standalone: false,
})
export class QuestionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(QuestionComponent) questionComponent?: QuestionComponent;
  @ViewChild('pageScroller') pageScroller?: ElementRef<HTMLElement>;

  protected readonly savingNext = signal(false);
  protected readonly savingPrevious = signal(false);
  protected readonly finishing = signal(false);
  protected readonly loading = computed(
    () => this.savingNext() || this.savingPrevious() || this.finishing(),
  );
  protected readonly question = this.questionsService.currentQuestion;
  protected readonly selectedLanguage =
    this.translationEventService.selectedLanguage;

  private destroy$ = new Subject<void>();
  private readonly questionRequestTimeoutMs = 10000;
  private readonly activeQuestionnaireInstanceGuidStorageKey =
    'activeQuestionnaireInstanceGuid';
  // Tracks the prior selected language so we only refresh on explicit user
  // language changes, not on initial signal/bootstrap emissions.
  private lastSelectedLanguageCode: string | null = null;

  constructor(
    private questionsService: QuestionsService,
    private router: Router,
    private questionnaireInstanceService: QuestionnaireInstanceService,
    private toastService: ToastService,
    private translationEventService: TranslationEventService,
  ) {
    effect(() => {
      const language = this.translationEventService.selectedLanguage();
      const questionLanguage = this.question()?.language;
      const guid = this.questionnaireInstanceService.questionnaireInstanceGuid;

      if (!language || !guid) {
        return;
      }

      if (this.lastSelectedLanguageCode === null) {
        this.lastSelectedLanguageCode = language.code;
        return;
      }

      const languageWasChanged =
        language.code !== this.lastSelectedLanguageCode;
      this.lastSelectedLanguageCode = language.code;

      if (!languageWasChanged) {
        return;
      }

      if (language.code !== questionLanguage) {
        this.questionsService
          .refreshCurrentQuestion(guid)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastService.success('Language changed to ' + language.name);
            },
            error: (err) => {
              console.error('Failed to refresh current question:', err);
              this.toastService.error(
                'Could not change language. Please try again.',
              );
            },
          });
      }
    });
  }

  ngOnInit() {
    console.info('QuestionsComponent initialised');
    this.translationEventService.reset();
    this.restoreQuestionnaireInstanceGuid();
  }

  ngAfterViewInit() {
    console.info('QuestionComponent initialised:', this.questionComponent);
    this.resetScrollPosition();
  }

  /*
    Save the answer to the current question, update the current question to the next in the sequence as returned
    from the server. If there is no next question, move to the finish screen
  */
  goToNextQuestion() {
    if (this.savingNext()) {
      return;
    }

    if (!this.questionComponent) {
      console.error('QuestionComponent is not initialised');
      return;
    }

    this.savingNext.set(true);
    this.questionsService
      .goToNextQuestion(
        this.questionnaireInstanceService.questionnaireInstanceGuid!,
        this.questionComponent.getAnswerModel(),
      )
      .pipe(
        timeout(this.questionRequestTimeoutMs),
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error going to next question:', err);
          this.toastService.error(
            'Could not save your answer. Please try again.',
          );
          return EMPTY;
        }),
        finalize(() => this.savingNext.set(false)),
      )
      .subscribe({
        next: (nextQuestion) => {
          if (nextQuestion) {
            this.questionsService.setCurrentQuestion(nextQuestion);
            this.resetScrollPosition();
          } else {
            this.finishQuestionnaire();
          }
        },
      });
  }

  handleAutoAdvanceSingleChoice() {
    if (!this.questionComponent?.canMoveToNextQuestion || this.loading()) {
      return;
    }

    this.goToNextQuestion();
  }

  /*
    Go to the previous question in the questionnaire
  */
  goToPreviousQuestion() {
    if (this.savingPrevious()) {
      return;
    }

    this.savingPrevious.set(true);
    this.questionsService
      .goToPreviousQuestion(
        this.questionnaireInstanceService.questionnaireInstanceGuid!,
      )
      .pipe(
        timeout(this.questionRequestTimeoutMs),
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error going to previous question:', err);
          this.toastService.error(
            'Could not load the previous question. Please try again.',
          );
          return EMPTY;
        }),
        finalize(() => this.savingPrevious.set(false)),
      )
      .subscribe({
        next: (nextQuestion) => {
          if (nextQuestion) {
            this.questionsService.setCurrentQuestion(nextQuestion);
            this.resetScrollPosition();
          }
        },
      });
  }

  /*
    Finish the questionnaire, by updating the status to completed, and navigating the user to the completed view
  */
  finishQuestionnaire() {
    if (this.finishing()) {
      return;
    }

    this.finishing.set(true);
    this.questionnaireInstanceService
      .finishPost({
        questionnaireInstanceGuid:
          this.questionnaireInstanceService.questionnaireInstanceGuid!,
      })
      .pipe(
        timeout(this.questionRequestTimeoutMs),
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error finishing questionnaire:', err);
          this.toastService.error(
            'Could not finish the questionnaire. Please try again.',
          );
          this.finishing.set(false);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (res) => {
          // Questionnaire finish redirect trust boundary is backend-owned.
          // Only Tirohia responses should include an external redirectUrl;
          // other questionnaires fall back to the local completed page.
          if (res.redirectUrl) {
            this.redirectToBackendFinishUrl(res.redirectUrl);
          } else {
            void this.router.navigateByUrl('completed');
          }
        },
      });
  }

  protected redirectToBackendFinishUrl(redirectUrl: string): void {
    window.location.href = redirectUrl;
  }

  // Prevent user from leaving the page
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.finishing()) {
      event.preventDefault();
    }
  }

  ngOnDestroy() {
    console.info('QuestionsComponent destroyed');
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetScrollPosition(): void {
    requestAnimationFrame(() => {
      this.pageScroller?.nativeElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }

  private restoreQuestionnaireInstanceGuid(): void {
    const guid =
      this.questionnaireInstanceService.questionnaireInstanceGuid ??
      this.router.getCurrentNavigation()?.extras.state?.[
        'activeQuestionnaireInstanceGuid'
      ] ??
      sessionStorage.getItem(this.activeQuestionnaireInstanceGuidStorageKey);

    if (guid) {
      this.questionnaireInstanceService.questionnaireInstanceGuid = guid;
      sessionStorage.setItem(
        this.activeQuestionnaireInstanceGuidStorageKey,
        guid,
      );
    }
  }
}
